import { Component, Element, Event, EventEmitter, Fragment, Host, h, Prop, State, Watch, Method, forceUpdate, Listen } from '@stencil/core';
import { IntlShape } from '@formatjs/intl';
import parseJson from '../../utils/parse-json';
import { state, onChange } from '../../store';
import shadowRootParentOrDocument from '../../utils/generic-functions';
import getIdentityHome from '../../utils/identity-urls';
import fetchWithRetry, { ResultErrorVariant } from '../../utils/retry';

import {
  getAccountUrl,
  getFeatureFlag,
  getShouldUseAccountOwdFeatureFlagData,
  getGlobalSupportPortalUrl,
  getSupercupFeatureSpec,
  getNavigationFeatureSpec,
} from '../../utils/header-footer';

import { LocaleSwitcherOption, UserObject, SNavigateEvent, AccountMenu } from '../../types/types';
import getDataserviceUrl from '../../utils/dataservice-urls';

@Component({
  tag: 'aui-header',
  styleUrl: 'aui-header.scss',
  shadow: false,
})
export class AuiHeader {
  @Listen('s-intlupdate', { target: 'window' })
  intlUpdateHandler() {
    forceUpdate(this);
  }

  @Element() element: HTMLAuiHeaderElement;

  @Prop() user: UserObject | string;

  @Prop({ attribute: 'sidenavheader', reflect: true }) sideNavHeader = false;

  @Prop({ attribute: 'changepasswordurl' }) changePasswordUrl?: string;

  @Prop({ attribute: 'productname' }) productName: string;

  @Prop({ attribute: 'homeurl' }) homeUrl?: string = 'https://www.avalara.com';

  @Prop({ attribute: 'loginurl' }) loginUrl?: string = AuiHeader.identityHome;

  @Prop({ attribute: 'logouturl' }) logoutUrl?: string;

  @Prop({ attribute: 'appkey' }) appKey: string;

  @Prop({ attribute: 'integratehelp' }) integrateHelp?: boolean = false;

  @Prop({ attribute: 'subjectid' }) subjectId?: string;

  @Prop({ attribute: 'localeswitcheroptionslist' }) localeSwitcherOptionsList?: LocaleSwitcherOption[] | string;

  @Prop({ attribute: 'integratelocaleswitcher' }) integrateLocaleSwitcher?: boolean = false;

  @Prop({ attribute: 'shoulduseaccountowd' }) shouldUseAccountOwd?: boolean = false;

  @Prop({ attribute: 'showchatbutton' }) showChatButton?: boolean = false;

  @Prop({ attribute: 'i18n' }) i18n?: Record<string, string> | string = {};

  @Prop() requestCurrentAccessToken: () => Promise<{ accessToken: string }>;

  @State() showSamlSettings = false;

  @State() shouldUseAccountOwdState = this.shouldUseAccountOwd;

  @State() isZoomInInitialized = !!window.iph;

  @State() userJson: UserObject = { givenName: '', familyName: '' };

  @State() accountMenuItems: AccountMenu;

  @State() showChat = false;

  @State() i18nJson: Record<string, string> = {};

  @State() localeSwitcherOptionsListJson: LocaleSwitcherOption[];

  @State() enableAccountMenuChanges = false;

  #zoomInInterval;

  @Event({
    eventName: 's-navigate',
    bubbles: true,
    composed: true,
  })
  sNavigate: EventEmitter<SNavigateEvent>;

  @Event({
    eventName: 's-chat',
    bubbles: true,
    composed: true,
  })
  sChat: EventEmitter<object>;

  async #fetchFeatureFlagShowChatInGlobalHeader() {
    const feature = {
      featureSpec: getNavigationFeatureSpec(),
      featureName: 'show-chat-in-global-header',
      featureData: { avataxAccountId: this.accountId.toString() },
    };
    return getFeatureFlag({
      feature,
      setFlagValue: flagValue => {
        this.showChat = flagValue;
      },
    });
  }

  async #fetchFeatureFlagShowSamlSettings() {
    const feature = {
      featureSpec: getSupercupFeatureSpec(),
      featureName: 'show-saml-settings',
      featureData: { subjectId: this.userJson?.subjectId },
    };
    return getFeatureFlag({
      feature,
      setFlagValue: flagValue => {
        this.showSamlSettings = flagValue;
      },
    });
  }

  async #fetchFeatureFlagEnableAccountMenuChanges() {
    const feature = {
      featureSpec: getNavigationFeatureSpec(),
      featureName: 'enableAccountMenuChanges',
      featureData: { avataxAccountId: this.accountId.toString() },
    };
    return getFeatureFlag({
      feature,
      setFlagValue: flagValue => {
        this.enableAccountMenuChanges = flagValue;
      },
    });
  }

  async #fetchFeatureFlagShouldUseAccountOwd() {
    const feature = getShouldUseAccountOwdFeatureFlagData();
    return getFeatureFlag({
      feature,
      setFlagValue: flagValue => {
        this.shouldUseAccountOwdState = flagValue;
      },
    });
  }

  @Watch('i18n')
  watchI18n() {
    this.i18nJson = parseJson(this.i18n, "Please provide valid JSON string on header's i18n attribute", {});
  }

  @Watch('subjectId')
  watchSubjectId() {
    return this.#fetchFeatureFlagShowSamlSettings();
  }

  @Watch('shouldUseAccountOwd')
  watchShouldUseAccountOwd(shouldUseAccountOwdOverride) {
    this.shouldUseAccountOwdState = !!shouldUseAccountOwdOverride;
  }

  @Watch('user')
  watchUser(newJson) {
    const errorMessage = "Please provide valid JSON string on aui-header's user attribute";
    const userJson = parseJson(newJson, errorMessage, {});
    if (userJson?.subjectId) {
      this.#fetchFeatureFlagShowSamlSettings();
    }
    this.userJson = userJson;

    if (this.accountId) {
      this.#fetchFeatureFlagShowChatInGlobalHeader();
    }

    // TODO: Uncomment when locale switcher is implemented in Stencil
    // this.#localeSwitcherEl.user = newJson;
  }

  @Watch('requestCurrentAccessToken')
  watchRequestCurrentAccessToken() {
    if (this.requestCurrentAccessToken) {
      this.#fetchFeatureFlagEnableAccountMenuChanges();
      this.getAccountMenuJson();
    }
  }

  @Watch('changePasswordUrl')
  watchChangePasswordUrl(changePasswordUrl) {
    if (this.accountMenuItems) {
      this.#updateUrl('aui-header.account-menu.change-password', changePasswordUrl);
    }
  }

  @Watch('logoutUrl')
  watchLogoutUrl(logoutUrl) {
    if (this.accountMenuItems) {
      this.#updateUrl('aui-header.account-menu.logout', logoutUrl);
    }
  }

  @Watch('localeSwitcherOptionsList')
  watchLocaleSwitcherOptionsList() {
    const errorMessage = "Please provide valid JSON string on aui-header's localeSwitcherOptionsList.";
    this.localeSwitcherOptionsListJson = parseJson(this.localeSwitcherOptionsList, errorMessage);
  }

  @Watch('showChatButton')
  watchShowChatButton() {
    this.#fetchFeatureFlagShowChatInGlobalHeader();
  }

  #updateUrl(dataRef, newUrl) {
    const item = this.accountMenuItems.userData?.menuItems?.find(menu => menu.dataRef === dataRef);
    if (item) {
      item.url = newUrl;
    }
  }

  async #getAccessToken() {
    const json = await this.requestCurrentAccessToken();
    if (!json) {
      throw new Error('Could not get accessToken from server');
    }
    return json.accessToken;
  }

  @Method()
  async getAccountMenuJson() {
    try {
      const accessToken = await this.#getAccessToken();

      const response = await fetchWithRetry<AccountMenu>({
        url: `${getDataserviceUrl(window.aui.env)}/api/v1/account-menu`,
        options: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
        retries: AuiHeader.FETCH_RETRIES_MAX,
        retryDelay: AuiHeader.FETCH_RETRY_DELAY,
      });

      if (!response.ok) {
        const { error } = response as ResultErrorVariant;
        console.error('Failed to fetch account menu:', error);
        return;
      }

      this.accountMenuItems = response.value;

      if (this.changePasswordUrl && response.value.userData) {
        this.#updateUrl('aui-header.account-menu.change-password', this.changePasswordUrl);
      }
      if (this.logoutUrl && response.value.userData) {
        this.#updateUrl('aui-header.account-menu.logout', this.logoutUrl);
      }
    } catch (err) {
      console.error('Failed to fetch account menu:', err);
    }
  }

  intl: IntlShape;

  static get identityHome() {
    return getIdentityHome(window.aui.env);
  }

  static get documentLockerUrl() {
    const pathname = '/mylockers/downloads';
    switch (window.aui.env) {
      case 'local':
      case 'ci':
      case 'dev':
      case 'qa':
        return `https://qa.documentlocker.avalara.io${pathname}`;
      case 'sbx':
        return `https://sbx.documentlocker.avalara.com${pathname}`;
      case 'prod':
      case 'prd':
      default:
        return `https://documentlocker.avalara.com${pathname}`;
    }
  }

  static get FETCH_RETRIES_MAX() {
    return 3;
  }

  static get FETCH_RETRY_DELAY() {
    return 500;
  }

  static get FETCH_ZOOMIN_RETRIES_MAX() {
    return 20;
  }

  toggleSidenavVisibility(e) {
    const sidenav = shadowRootParentOrDocument(this.element).querySelector('aui-sidenav');
    const button = e.composedPath()[0].closest('button');
    sidenav.classList.toggle('open');
    if (sidenav.classList.contains('open')) {
      button.classList.add('open');
      button.querySelector("s-icon[name='menu']").classList.add('hidden');
      button.querySelector("s-icon[name='close']").classList.remove('hidden');
    } else {
      button.classList.remove('open');
      button.querySelector("s-icon[name='menu']").classList.remove('hidden');
      button.querySelector("s-icon[name='close']").classList.add('hidden');
    }
  }

  get switchAccountsUrl() {
    if (this.userJson?.switchAccountsUrl) return this.userJson?.switchAccountsUrl;

    let baseUrl;
    const isAccountApp = this.appKey === 'account';
    const appKey = isAccountApp ? 'account' : 'admin';

    switch (window.aui.env) {
      case 'local':
        baseUrl = isAccountApp ? 'https://local.account.avalara.io' : 'http://localhost:4200';
        break;
      case 'ci':
      case 'dev':
        baseUrl = `https://ci.${appKey}.avalara.io`;
        break;
      case 'qa':
      case 'uat':
        baseUrl = `https://qa.${appKey}.avalara.io`;
        break;
      case 'sbx':
        baseUrl = `https://sandbox.${appKey}.avalara.com`;
        break;
      case 'prod':
      default:
        baseUrl = `https://${appKey}.avalara.com`;
    }

    return isAccountApp ? `${baseUrl}/#/goTo/select-account` : `${baseUrl}/cup/accounts`;
  }

  get isLoggedIn() {
    return this.userJson && this.userJson?.isLoggedIn;
  }

  // Prefer accountId if passed, default to delivery system specific ids
  get accountId() {
    return this.userJson?.accountId || this.userJson.avataxAccountId || '';
  }

  // Prefer avataxAccountId if passed, default to accountId if present
  get avataxAccountId() {
    return this.userJson?.avataxAccountId || this.userJson?.accountId || '';
  }

  get hrefs() {
    const sdashAssetPath = 'https://assets.avalara.com/skylab-sdk/latest';
    const accountUrl = getAccountUrl(this.shouldUseAccountOwdState);
    const loggedInSupportCasesUrl = getGlobalSupportPortalUrl();

    return {
      billing: this.isLoggedIn ? `${accountUrl}/a/${this.avataxAccountId}/billing` : '#',
      changePassword: this.changePasswordUrl || `${AuiHeader.identityHome}/changePassword`,
      logIn: this.loginUrl || AuiHeader.identityHome,
      logOut: this.logoutUrl || '/auth/logout',
      logoLarge: `${sdashAssetPath}/images/avalara-logo.png`,
      logoSmall: `${sdashAssetPath}/images/app-icon-32x32.png`,
      productHome: this.homeUrl || 'https://www.avalara.com',
      samlSettings: `${accountUrl}/saml-settings`,
      supportCases: this.isLoggedIn ? loggedInSupportCasesUrl : 'https://help.avalara.com/Contact_Avalara/Submit_a_Case',
      switchAccounts: this.switchAccountsUrl,
      productsUsage: `${accountUrl}/goTo/products`,
    };
  }

  get #avalaraLogoHtml() {
    if (this.sideNavHeader) {
      return (
        <a class="flex align-items-center" href={this.hrefs.productHome} data-ref="aui-header.logo-product">
          <img src={this.hrefs.logoLarge} alt={this.#headerText.logoAltText} class="hide-md" />
          <img src={this.hrefs.logoSmall} alt={this.#headerText.logoAltText} class="margin-start-xs show-md" />
          {this.productName ? (
            <span data-ref="product-name" class="alt margin-bottom-none hide-md">
              {this.productName}
            </span>
          ) : (
            ''
          )}
        </a>
      );
    }
    return (
      <a class="flex align-items-center" href={this.hrefs.productHome} data-ref="aui-header.logo-product">
        <img src={this.hrefs.logoLarge} alt={this.#headerText.logoAltText} class="hide-xs" />
        <img src={this.hrefs.logoSmall} alt={this.#headerText.logoAltText} class="show-xs" />
        {this.productName ? (
          <span data-ref="product-name" class="alt margin-bottom-none hide-sm">
            {this.productName}
          </span>
        ) : (
          ''
        )}
      </a>
    );
  }

  get #legacyAccountMenuItemsHtml() {
    return (
      <div slot="menu-items" data-ref="legacy-menu">
        <div class="section-header" data-ref="aui-header.account-menu.id">
          <span class="font-semibold margin-end-xs">{this.#headerText.accountIdLabel}</span>
          <span>{this.accountId}</span>
        </div>

        <a href={this.hrefs.productsUsage} data-ref="aui-header.account-menu.products-usage">
          <s-icon name="usage" class="pad-end-xs" aria-hidden="true"></s-icon>
          {this.#headerText.productsandUsageLabel}
        </a>
        <a href={this.hrefs.billing} data-ref="aui-header.account-menu.billing">
          <s-icon name="usd-circle" class="pad-end-xs" aria-hidden="true"></s-icon>
          {this.#headerText.billingLabel}
        </a>
        <a href={this.hrefs.supportCases} data-ref="aui-header.account-menu.support-cases">
          <s-icon name="support" class="pad-end-xs" aria-hidden="true"></s-icon>
          {this.#headerText.supportCasesLabel}
        </a>
        <hr role="separator" data-ref="account-features-separator" />
        <div class="section-header">
          {this.userJson?.name ? (
            <div>
              <span class="font-semibold margin-end-xs">{this.#headerText.usernameLabel} </span>
              <span>{this.userJson?.name}</span>
            </div>
          ) : (
            <div>
              <span class="font-semibold margin-end-xs">{this.#headerText.nameLabel}</span>
              <span>
                {this.userJson?.givenName} {this.userJson?.familyName}
              </span>
            </div>
          )}
          {this.userJson?.avataxUserId ? (
            <div>
              <span class="font-semibold margin-end-xs">{this.#headerText.userIdLabel}</span>
              <span>{this.userJson?.avataxUserId}</span>
            </div>
          ) : (
            ''
          )}
        </div>
        <a href={AuiHeader.documentLockerUrl} data-ref="aui-header.account-menu.downloads">
          <s-icon name="download" class="pad-end-xs" aria-hidden="true"></s-icon>
          {this.#headerText.myDownloadsLabel}
        </a>
        <a href={this.hrefs.changePassword} data-ref="aui-header.account-menu.change-password">
          <s-icon name="key" class="pad-end-xs" aria-hidden="true"></s-icon>
          {this.#headerText.changePasswordLabel}
        </a>
        <a href={this.hrefs.logOut} data-ref="aui-header.account-menu.logout">
          <s-icon name="logout" class="pad-end-xs" aria-hidden="true"></s-icon>
          {this.#headerText.signOutLabel}
        </a>
        {this.userJson?.isTechSupport ? (
          <Fragment>
            <hr role="separator" />
            <div class="section-header">
              <span class="font-semibold">{this.#headerText.internalOnlyLabel}</span>
            </div>
            <a
              href={this.hrefs.switchAccounts as string}
              data-ref="aui-header.account-menu.tsa-switch"
              onClick={this.userJson?.emitSwitchAccountsNavEvent ? this.#emitNavEvent : null}
            >
              <s-icon name="switch-accounts" class="pad-end-xs" aria-hidden="true"></s-icon>
              {this.#headerText.switchAccountsLabel}
            </a>
            {this.showSamlSettings === true ? (
              <a href={this.hrefs.samlSettings} data-ref="aui-header.account-menu.saml-settings">
                <s-icon name="security" class="pad-end-xs" aria-hidden="true"></s-icon>
                {this.#headerText.samlSettingsLabel}
              </a>
            ) : (
              ''
            )}
          </Fragment>
        ) : (
          ''
        )}
      </div>
    );
  }

  get #headerText() {
    const { intl } = this;
    return {
      chatButton:
        this.i18nJson['auiHeader.chatButton'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.chatButton',
          defaultMessage: 'Chat',
        }),
      chatButtonNewTag:
        this.i18nJson['auiHeader.chatButtonNewTag'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.chatButtonNewTag',
          defaultMessage: 'New',
        }),
      helpButton:
        this.i18nJson['auiHeader.helpButton'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.helpButton',
          defaultMessage: 'Help',
        }),
      sidenavToggle:
        this.i18nJson['auiHeader.ariaLabel.sidenavToggle'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.ariaLabel.sidenavToggle',
          defaultMessage: 'Open/close navigation menu',
        }),
      logoAltText:
        this.i18nJson['auiHeader.logoAltText'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.logoAltText',
          defaultMessage: 'Avalara logo',
        }),
      accountIdLabel:
        this.i18nJson['auiHeader.accountIdLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.accountIdLabel',
          defaultMessage: 'Account ID:',
        }),
      productsandUsageLabel:
        this.i18nJson['auiHeader.productsandUsageLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.productsandUsageLabel',
          defaultMessage: 'Products and usage',
        }),
      billingLabel:
        this.i18nJson['auiHeader.billingLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.billingLabel',
          defaultMessage: 'Billing',
        }),
      supportCasesLabel:
        this.i18nJson['auiHeader.supportCasesLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.supportCasesLabel',
          defaultMessage: 'Support cases',
        }),
      usernameLabel:
        this.i18nJson['auiHeader.usernameLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.usernameLabel',
          defaultMessage: 'Username:',
        }),
      nameLabel:
        this.i18nJson['auiHeader.nameLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.nameLabel',
          defaultMessage: 'Name:',
        }),
      userIdLabel:
        this.i18nJson['auiHeader.userIdLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.userIdLabel',
          defaultMessage: 'User ID:',
        }),
      myDownloadsLabel:
        this.i18nJson['auiHeader.myDownloadsLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.myDownloadsLabel',
          defaultMessage: 'My downloads',
        }),
      changePasswordLabel:
        this.i18nJson['auiHeader.changePasswordLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.changePasswordLabel',
          defaultMessage: 'Change password',
        }),
      signInLabel:
        this.i18nJson['auiHeader.signInLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.signInLabel',
          defaultMessage: 'Sign in',
        }),
      signOutLabel:
        this.i18nJson['auiHeader.signOutLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.signOutLabel',
          defaultMessage: 'Sign out',
        }),
      internalOnlyLabel:
        this.i18nJson['auiHeader.internalOnlyLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.internalOnlyLabel',
          defaultMessage: 'Internal Only',
        }),
      switchAccountsLabel:
        this.i18nJson['auiHeader.switchAccountsLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.switchAccountsLabel',
          defaultMessage: 'Switch accounts',
        }),
      samlSettingsLabel:
        this.i18nJson['auiHeader.samlSettingsLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.samlSettingsLabel',
          defaultMessage: 'SAML settings',
        }),
      accountLabel:
        this.i18nJson['auiHeader.accountLabel'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.accountLabel',
          defaultMessage: 'Account',
        }),
      knowledgeCenter:
        this.i18nJson['auiHeader.knowledgeCenter'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.knowledgeCenter',
          defaultMessage: 'Knowledge Center',
        }),
      avalaraUniversity:
        this.i18nJson['auiHeader.avalaraUniversity'] ||
        intl.formatMessage({
          id: 'skylab.auiHeader.avalaraUniversity',
          defaultMessage: 'Avalara University',
        }),
    };
  }

  get #sidenavTriggerHtml() {
    if (this.sideNavHeader) {
      return (
        <button
          type="button"
          data-ref="sidenav-trigger"
          aria-label={this.#headerText.sidenavToggle}
          class="ghost icon small show-md"
          onClick={e => this.toggleSidenavVisibility(e)}
        >
          <s-icon name="menu" aria-hidden="true"></s-icon>
          <s-icon name="close" class="hidden" aria-hidden="true"></s-icon>
        </button>
      );
    }
    return ``;
  }

  static buildMenuItems(menuList) {
    return menuList.map(menuItem => {
      return (
        <a href={menuItem.url} data-ref={menuItem.dataRef} target={menuItem.target} key={menuItem.dataRef}>
          <s-icon name={menuItem.icon} class="pad-end-xs" aria-hidden="true"></s-icon>
          {menuItem.label}
        </a>
      );
    });
  }

  get #accountMenuItemsFromDataserviceHtml() {
    const { accountData, userData, internalOnlyData } = this.accountMenuItems;

    return (
      <div slot="menu-items" data-ref="dataservice-menu">
        <div class="section-header" data-ref="aui-header.account-menu.id">
          <span class="font-semibold margin-end-xs">{this.#headerText.accountIdLabel}</span>
          <span>{accountData?.accountId}</span>
        </div>
        {accountData?.menuItems ? AuiHeader.buildMenuItems(accountData.menuItems) : ''}
        <hr role="separator" data-ref="account-features-separator" />
        <div class="section-header">
          <div>
            <span class="font-semibold margin-end-xs">{this.#headerText.usernameLabel}</span>
            <span>{userData?.userName}</span>
          </div>
          {this.enableAccountMenuChanges ? (
            ''
          ) : (
            <Fragment>
              <div>
                <span class="font-semibold margin-end-xs">{this.#headerText.userIdLabel}</span>
                <span>{userData?.userId}</span>
              </div>
            </Fragment>
          )}
        </div>
        {userData?.menuItems ? AuiHeader.buildMenuItems(userData.menuItems) : ''}
        {internalOnlyData ? (
          <Fragment>
            <hr role="separator" />
            <div class="section-header">
              <span class="font-semibold">{this.#headerText.internalOnlyLabel}</span>
            </div>
            {AuiHeader.buildMenuItems(internalOnlyData)}
          </Fragment>
        ) : (
          ''
        )}
      </div>
    );
  }

  get #accountMenuHtml() {
    if (this.sideNavHeader) {
      return this.accountMenuItems || this.userJson?.isLoggedIn ? (
        <s-menu data-ref="account-menu" aria-label={this.#headerText.accountLabel}>
          <button type="button" slot="menu-trigger" class="ghost icon-leading collapses" data-ref="aui-header.account-menu.trigger">
            <s-icon name="user-circle" aria-hidden="true"></s-icon>
            <span class="hide-sm">{this.#headerText.accountLabel}</span>
          </button>
          {this.accountMenuItems ? this.#accountMenuItemsFromDataserviceHtml : this.#legacyAccountMenuItemsHtml}
        </s-menu>
      ) : (
        <a class="ghost button collapses" href={this.hrefs.logIn} data-ref="aui-header.account.login">
          <div class="inline-flex align-items-center justify-content-center margin-end-xs" data-ref="icon-container">
            <s-icon name="user-circle" aria-hidden="true"></s-icon>
          </div>
          <span class="hide-sm">{this.#headerText.signInLabel}</span>
        </a>
      );
    }
    return this.accountMenuItems || this.userJson?.isLoggedIn ? (
      <s-menu class="nav-menu" data-ref="account-menu" aria-label={this.#headerText.accountLabel}>
        <button type="button" slot="menu-trigger" class="ghost" data-ref="aui-header.account-menu.trigger">
          <div class="inline-flex align-items-center justify-content-center margin-end-xs" data-ref="icon-container">
            {this.accountMenuItems?.userInitials || this.#getUserInitials()}
          </div>
          {this.#headerText.accountLabel}
        </button>
        {this.accountMenuItems ? this.#accountMenuItemsFromDataserviceHtml : this.#legacyAccountMenuItemsHtml}
      </s-menu>
    ) : (
      <a class="ghost button" href={this.hrefs.logIn} data-ref="aui-header.account.login">
        <div class="inline-flex align-items-center justify-content-center margin-end-xs" data-ref="icon-container">
          <s-icon name="user-filled" aria-hidden="true"></s-icon>
        </div>
        {this.#headerText.signInLabel}
      </a>
    );
  }

  get #helpTriggerHtml() {
    if (this.sideNavHeader) {
      if (this.isZoomInInitialized) {
        return (
          <button type="button" data-ref="zoom-in-help" class="ghost icon-leading collapses" onClick={AuiHeader.toggleZoomInWidget}>
            <s-icon name="help-circle" aria-hidden="true"></s-icon>
            <span class="hide-sm">{this.#headerText.helpButton}</span>
          </button>
        );
      }
      return (
        <s-menu data-ref="default-help-menu" aria-label={this.#headerText.helpButton}>
          <button type="button" slot="menu-trigger" class="ghost icon-leading collapses" aria-haspopup="true">
            <s-icon name="help-circle" aria-hidden="true"></s-icon>
            <span class="hide-sm">{this.#headerText.helpButton}</span>
          </button>
          <ul slot="menu-items">
            <li>
              <a href="https://knowledge.avalara.com/" target="_blank" rel="noopener">
                <s-icon name="help-circle" class="pad-end-sm" aria-hidden="true"></s-icon>
                {this.#headerText.knowledgeCenter}
              </a>
            </li>
            <li>
              <a href="https://training.avalara.com/" target="_blank" rel="noopener">
                <s-icon name="grad-cap" class="pad-end-sm" aria-hidden="true"></s-icon>
                {this.#headerText.avalaraUniversity}
              </a>
            </li>
          </ul>
        </s-menu>
      );
    }

    if (this.isZoomInInitialized) {
      return (
        <button type="button" data-ref="zoom-in-help" class="ghost icon-leading" onClick={AuiHeader.toggleZoomInWidget}>
          <s-icon name="help-circle" aria-hidden="true"></s-icon>
          {this.#headerText.helpButton}
        </button>
      );
    }
    return (
      <s-menu data-ref="default-help-menu" aria-label={this.#headerText.helpButton}>
        <button type="button" slot="menu-trigger" class="ghost icon-leading" aria-haspopup="true">
          <s-icon name="help-circle" aria-hidden="true"></s-icon>
          {this.#headerText.helpButton}
        </button>
        <ul slot="menu-items">
          <li>
            <a href="https://knowledge.avalara.com/" target="_blank" rel="noopener">
              <s-icon name="help-circle" class="pad-end-sm" aria-hidden="true"></s-icon>
              {this.#headerText.knowledgeCenter}
            </a>
          </li>
          <li>
            <a href="https://training.avalara.com/" target="_blank" rel="noopener">
              <s-icon name="grad-cap" class="pad-end-sm" aria-hidden="true"></s-icon>
              {this.#headerText.avalaraUniversity}
            </a>
          </li>
        </ul>
      </s-menu>
    );
  }

  #emitChatEvent = () => {
    this.sChat.emit({});
  };

  get #chatHtml() {
    if (this.showChatButton && this.showChat) {
      return (
        <button type="button" data-ref="ipua-chat" class="ghost icon-leading collapses" onClick={this.#emitChatEvent}>
          <s-icon name="chat-2" aria-hidden="true"></s-icon>
          <span class="hide-sm">{this.#headerText.chatButton}</span>
        </button>
      );
    }
    return '';
  }

  get localeSwitcherHtml() {
    return (
      <aui-locale-switcher optionsList={this.localeSwitcherOptionsListJson} user={this.userJson} requestCurrentAccessToken={this.requestCurrentAccessToken}></aui-locale-switcher>
    );
  }

  static toggleZoomInWidget = () => {
    if (!window.iph) {
      console.error('ZoomIN iph widget is not initialized');
      return;
    }
    window.iph('toggle');
  };

  #getUserInitials() {
    const givenName = this.userJson?.givenName as string;
    const familyName = this.userJson?.familyName as string;
    if (givenName && familyName) return `${givenName.trim()[0]}${familyName.trim()[0]}`;
    return 'A';
  }

  #emitNavEvent = e => {
    e.preventDefault();
    const target = e.composedPath()[0];
    this.sNavigate.emit({
      label: target.innerText,
      url: target.getAttribute('href'),
      dataRef: target.getAttribute('data-ref'),
    });
  };

  componentWillLoad() {
    // Skip fetching the feature flag if an explicit override attribute is passed
    if (this.shouldUseAccountOwdState) {
      return Promise.resolve();
    }

    return this.#fetchFeatureFlagShouldUseAccountOwd();
  }

  componentWillUpdate() {
    state.intl
      .then(value => {
        this.intl = value as IntlShape;
      })
      .catch(err => {
        console.error('Error creating intl object:', err);
      });
  }

  connectedCallback() {
    onChange('locale', () => {
      forceUpdate(this);
    });
    state.intl
      .then(value => {
        this.intl = value as IntlShape;
      })
      .catch(err => {
        console.error('Error creating intl object:', err);
      });
    this.watchSubjectId();
    this.watchUser(this.user);
    this.watchRequestCurrentAccessToken();
    this.watchChangePasswordUrl(this.changePasswordUrl);
    this.watchLogoutUrl(this.logoutUrl);
    this.watchShowChatButton();
    this.watchLocaleSwitcherOptionsList();

    // Adding interval to check if ZoomIN is intialized
    let fetchZoomINTimes = AuiHeader.FETCH_ZOOMIN_RETRIES_MAX;
    this.#zoomInInterval = setInterval(() => {
      if (window.iph) {
        this.isZoomInInitialized = true;
        clearInterval(this.#zoomInInterval);
      }
      if (fetchZoomINTimes === 0) clearInterval(this.#zoomInInterval);
      fetchZoomINTimes -= 1;
    }, AuiHeader.FETCH_RETRY_DELAY);
  }

  disconnectedCallback() {
    clearInterval(this.#zoomInInterval);
  }

  render() {
    if (!this.intl) return null;
    return (
      <Host>
        <header>
          <s-container class="flex justify-content-space align-items-center">
            <div class="flex">
              {this.#sidenavTriggerHtml} {this.#avalaraLogoHtml}
            </div>
            <div class="flex align-items-center">
              {this.integrateLocaleSwitcher ? this.localeSwitcherHtml : ''}
              {this.#chatHtml} {this.integrateHelp ? this.#helpTriggerHtml : ''} {this.#accountMenuHtml}
            </div>
          </s-container>
        </header>
      </Host>
    );
  }
}
